import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import toast from "react-hot-toast"
import {
  Archive,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  ChevronRight,
  Search,
  MoreVertical,
  Folder,
  Play,
  RefreshCw,
  ExternalLink,
  Globe,
  Settings,
  ToggleLeft,
  ToggleRight,
  Moon,
  Bell,
} from "lucide-react"

import { Button, Input } from "@aix/ui"
import { trpc } from "~/lib/trpc"

type EnvironmentType = "local" | "dev" | "prod"

// Environment config from synced project
interface EnvironmentInfo {
  url: string
  enabled: boolean
}

// Project config from DB (synced from source repos)
interface ProjectConfigInfo {
  project: string
  displayName: string
  environments?: {
    local?: EnvironmentInfo
    dev?: EnvironmentInfo
    prod?: EnvironmentInfo
  }
}

// Types for grouped scenarios
interface ScenarioGroup {
  key: string
  name: string
  project: string
  environment: string
  scenarios: ScenarioItem[]
  lastRun: Date | null
  passCount: number
  failCount: number
  totalCount: number
  projectConfig?: ProjectConfigInfo
}

// Schedule can be simple interval or with start time
type Schedule = string | { interval: string; startTime: string }

interface ScenarioItem {
  id: string
  scenarioId: string
  name: string
  project: string
  environment: "local" | "dev" | "prod"
  schedule: Schedule
  status: "healthy" | "failing" | "unknown" | "degraded"
  lastRun?: {
    createdAt: string
    duration: number
    success: boolean
    error?: string
  }
  stepCount: number
  critical: boolean
  // First endpoint for quick reference in list view
  firstEndpoint?: string
  // Base URL for the project (from synced config)
  baseUrl?: string
  // Recent run history for visualization (oldest first)
  recentRuns?: { success: boolean; duration: number }[]
  isSnoozed?: boolean
  snoozedUntil?: string | null
}

/**
 * API Monitors Dashboard
 * Displays health monitoring for all API endpoints with grouped view
 */
const STORAGE_KEY_EXPANDED = "monitors-expanded-groups"
const STORAGE_KEY_COLLAPSED = "monitors-collapsed-groups"

// Load collapsed groups from localStorage (we store collapsed instead of expanded for "all expanded by default")
function loadCollapsedGroups(): Set<string> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_COLLAPSED)
    if (stored) {
      return new Set(JSON.parse(stored))
    }
  } catch {
    // Ignore parse errors
  }
  return new Set()
}

// Save collapsed groups to localStorage
function saveCollapsedGroups(collapsed: Set<string>) {
  try {
    localStorage.setItem(STORAGE_KEY_COLLAPSED, JSON.stringify([...collapsed]))
  } catch {
    // Ignore storage errors
  }
}

function MonitorsScreen() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [projectFilter, setProjectFilter] = useState<string>("all")
  const [environmentFilter, setEnvironmentFilter] = useState<string>("all")
  const [showProjectConfig, setShowProjectConfig] = useState(false)

  // Store collapsed groups (inverse of expanded) - all groups expanded by default
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => loadCollapsedGroups())

  const utils = trpc.useUtils()

  // Fetch scenarios from DB
  const { data: dbScenarios, isLoading: dbLoading, refetch } = trpc.apiMonitors.listScenarios.useQuery()

  // Fetch code scenarios
  const { data: codeScenarios, isLoading: codeLoading } = trpc.apiMonitors.getCodeScenarios.useQuery()

  // Fetch project configs (contains environment URLs synced from source repos)
  const { data: projectConfigs, refetch: refetchProjects } = trpc.apiMonitors.listProjects.useQuery()

  // Trigger manual run mutation
  const triggerMutation = trpc.apiMonitors.triggerRun.useMutation({
    onSuccess: () => {
      void refetch()
    },
  })

  // Toggle environment mutation
  const toggleEnvMutation = trpc.apiMonitors.toggleEnvironment.useMutation({
    onSuccess: (data) => {
      toast.success(`${data.environment} ${data.enabled ? "enabled" : "disabled"} for ${data.project}`)
      void refetchProjects()
      void utils.apiMonitors.listProjects.invalidate()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to toggle environment")
    },
  })

  // Archive scenario mutation
  const archiveMutation = trpc.apiMonitors.archiveScenario.useMutation({
    onSuccess: () => {
      toast.success("Scenario archived")
      void refetch()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to archive scenario")
    },
  })

  // Snooze scenario mutation
  const snoozeMutation = trpc.apiMonitors.snoozeScenario.useMutation({
    onSuccess: (_data, variables) => {
      const v = variables as { duration?: string; id?: string } | undefined
      toast.success(`Snoozed for ${v?.duration ?? ""}`)
      void refetch()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to snooze scenario")
    },
  })

  // Unsnooze scenario mutation
  const unsnoozeMutation = trpc.apiMonitors.unsnoozeScenario.useMutation({
    onSuccess: () => {
      toast.success("Unsnoozed")
      void refetch()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to unsnooze scenario")
    },
  })

  // Handle archive - now uses UUID id
  const handleArchive = async (id: string) => {
    await archiveMutation.mutateAsync({ id })
  }

  const handleSnooze = async (id: string, duration: "1h" | "24h" | "7d") => {
    await snoozeMutation.mutateAsync({ id, duration })
  }

  const handleUnsnooze = async (id: string) => {
    await unsnoozeMutation.mutateAsync({ id })
  }

  // Helper to get base URL from project config
  const getProjectBaseUrl = (project: string): string | undefined => {
    const config = projectConfigs?.find((c) => c.project === project)
    // Prefer prod URL, fall back to dev, then local
    return config?.environments?.prod?.url || config?.environments?.dev?.url || config?.environments?.local?.url
  }

  // DB scenarios are the source of truth (each row is a scenario@env combo)
  const scenarios = useMemo(() => {
    if (!dbScenarios) return []

    return dbScenarios.map((dbSc) => {
      // Extract first endpoint from definition if available
      const definition = (dbSc as any)?.definition as { steps?: { request?: { endpoint?: string } }[] } | undefined
      const firstEndpoint = definition?.steps?.[0]?.request?.endpoint

      return {
        id: dbSc.id,
        scenarioId: dbSc.scenarioId,
        name: dbSc.name,
        project: dbSc.project,
        environment: dbSc.environment,
        schedule: dbSc.schedule || "1h",
        status: (dbSc.status as ScenarioItem["status"]) || "unknown",
        lastRun: dbSc.lastRun
          ? {
              createdAt:
                typeof dbSc.lastRun.createdAt === "string"
                  ? dbSc.lastRun.createdAt
                  : dbSc.lastRun.createdAt.toISOString(),
              duration: dbSc.lastRun.duration,
              success: dbSc.lastRun.success,
              error: dbSc.lastRun.error || undefined,
            }
          : undefined,
        stepCount: dbSc.stepCount || 0,
        critical: dbSc.critical || false,
        firstEndpoint,
        baseUrl: getProjectBaseUrl(dbSc.project),
        recentRuns: (dbSc as any)?.recentRuns || [],
        isSnoozed: (dbSc as any)?.isSnoozed ?? false,
        snoozedUntil: (dbSc as any)?.snoozedUntil ?? null,
      }
    })
  }, [dbScenarios, projectConfigs])

  // Calculate counts
  const passingCount = scenarios.filter((s) => s.status === "healthy").length
  const degradedCount = scenarios.filter((s) => s.status === "degraded").length
  const failingCount = scenarios.filter((s) => s.status === "failing").length

  // Get unique projects for filter
  const projects = useMemo(() => {
    const projectSet = new Set(scenarios.map((s) => s.project))
    return Array.from(projectSet)
  }, [scenarios])

  // Filter scenarios
  const filteredScenarios = useMemo(() => {
    return scenarios.filter((s) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        if (!s.name.toLowerCase().includes(query) && !s.project.toLowerCase().includes(query)) {
          return false
        }
      }
      // Status filter
      if (statusFilter !== "all") {
        if (statusFilter === "passing" && s.status !== "healthy") return false
        if (statusFilter === "failing" && s.status !== "failing") return false
        if (statusFilter === "degraded" && s.status !== "degraded") return false
      }
      // Project filter
      if (projectFilter !== "all" && s.project !== projectFilter) return false
      // Environment filter
      if (environmentFilter !== "all" && s.environment !== environmentFilter) return false
      return true
    })
  }, [scenarios, searchQuery, statusFilter, projectFilter, environmentFilter])

  // Create a map of project configs for quick lookup
  const projectConfigMap = useMemo(() => {
    const map = new Map<string, ProjectConfigInfo>()
    projectConfigs?.forEach((config) => {
      map.set(config.project, config as ProjectConfigInfo)
    })
    return map
  }, [projectConfigs])

  // Group scenarios by project (each scenario already has environment baked in)
  const groups = useMemo(() => {
    const groupMap = new Map<string, ScenarioGroup>()

    filteredScenarios.forEach((sc) => {
      const key = sc.project

      if (!groupMap.has(key)) {
        const config = projectConfigMap.get(sc.project)
        groupMap.set(key, {
          key,
          name: config?.displayName || `${sc.project.charAt(0).toUpperCase() + sc.project.slice(1)} API`,
          project: sc.project,
          environment: "", // Mixed environments now
          scenarios: [],
          lastRun: null,
          passCount: 0,
          failCount: 0,
          totalCount: 0,
          projectConfig: config,
        })
      }

      const group = groupMap.get(key)!
      group.scenarios.push(sc)
      group.totalCount++
      if (sc.status === "healthy") group.passCount++
      if (sc.status === "failing") group.failCount++
      if (sc.lastRun?.createdAt) {
        const runDate = new Date(sc.lastRun.createdAt)
        if (!group.lastRun || runDate > group.lastRun) {
          group.lastRun = runDate
        }
      }
    })

    return Array.from(groupMap.values())
  }, [filteredScenarios, projectConfigMap])

  // Toggle group expansion (inverted logic - we track collapsed, not expanded)
  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key) // Was collapsed, now expand
      } else {
        next.add(key) // Was expanded, now collapse
      }
      saveCollapsedGroups(next)
      return next
    })
  }, [])

  // Check if a group is expanded (not in collapsed set)
  const isGroupExpanded = useCallback((key: string) => !collapsedGroups.has(key), [collapsedGroups])

  // Handle trigger run - now uses UUID id (environment is implicit)
  const handleTriggerRun = async (id: string) => {
    await triggerMutation.mutateAsync({ id })
  }

  const isLoading = dbLoading || codeLoading

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Summary Cards */}
      <div className="p-4 grid grid-cols-3 gap-4">
        <SummaryCard
          label="PASSING"
          count={passingCount}
          color="green"
          onClick={() => setStatusFilter(statusFilter === "passing" ? "all" : "passing")}
          active={statusFilter === "passing"}
        />
        <SummaryCard
          label="DEGRADED"
          count={degradedCount}
          color="yellow"
          onClick={() => setStatusFilter(statusFilter === "degraded" ? "all" : "degraded")}
          active={statusFilter === "degraded"}
        />
        <SummaryCard
          label="FAILING"
          count={failingCount}
          color="red"
          onClick={() => setStatusFilter(statusFilter === "failing" ? "all" : "failing")}
          active={statusFilter === "failing"}
        />
      </div>

      {/* Project Environments Section */}
      <div className="px-4 pb-4">
        <button
          onClick={() => setShowProjectConfig(!showProjectConfig)}
          className="flex items-center gap-2 text-sm font-medium text-foreground mb-3 hover:text-primary transition-colors"
        >
          {showProjectConfig ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <Settings className="h-4 w-4" />
          Project Environments
        </button>

        {showProjectConfig && projectConfigs && projectConfigs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projectConfigs.map((config) => (
              <ProjectEnvironmentCard
                key={config.project}
                config={config}
                onToggle={(env, enabled) => {
                  toggleEnvMutation.mutate({
                    project: config.project,
                    environment: env,
                    enabled,
                  })
                }}
                isToggling={toggleEnvMutation.isLoading}
              />
            ))}
          </div>
        )}
      </div>

      {/* Search and Quick Filters */}
      <div className="px-4 pb-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="monitors-search-input"
              type="text"
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-secondary border-border text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* Quick Filter Chips */}
        <div className="flex items-center gap-4 text-xs">
          <span className="text-muted-foreground font-medium">Filters</span>

          {/* Environment */}
          <div className="flex items-center gap-1">
            {(["dev", "prod"] as const).map((env) => (
              <button
                key={env}
                onClick={() => setEnvironmentFilter(environmentFilter === env ? "all" : env)}
                className={`px-2.5 py-1 rounded-full font-medium transition-colors ${
                  environmentFilter === env
                    ? env === "prod"
                      ? "bg-green-500/20 text-green-400 ring-1 ring-green-500/40"
                      : "bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/40"
                    : "bg-secondary text-muted-foreground hover:text-foreground"
                }`}
              >
                {env}
              </button>
            ))}
          </div>

          <div className="w-px h-4 bg-border" />

          {/* Projects */}
          <div className="flex items-center gap-1 flex-wrap">
            {projects.map((p) => (
              <button
                key={p}
                onClick={() => setProjectFilter(projectFilter === p ? "all" : p)}
                className={`px-2.5 py-1 rounded-full font-medium transition-colors ${
                  projectFilter === p
                    ? "bg-primary/20 text-primary ring-1 ring-primary/40"
                    : "bg-secondary text-muted-foreground hover:text-foreground"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table Header */}
      <div className="px-4">
        <div className="flex items-center py-2 text-xs text-muted-foreground border-b border-border">
          <div className="flex-1 min-w-0">NAME</div>
          <div className="w-20 text-center">TYPE</div>
          <div className="w-28 text-center">STATUS</div>
          <div className="w-24 text-center">HISTORY</div>
          <div className="w-16 text-right">LATENCY</div>
          <div className="w-16 text-right">FREQ</div>
          <div className="w-10" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-3" />
            Loading scenarios...
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">No scenarios found</div>
        ) : (
          groups.map((group) => (
            <GroupRow
              key={group.key}
              group={group}
              isExpanded={isGroupExpanded(group.key)}
              onToggle={() => toggleGroup(group.key)}
              onTriggerRun={handleTriggerRun}
              onArchive={handleArchive}
              onSnooze={handleSnooze}
              onUnsnooze={handleUnsnooze}
              isPending={triggerMutation.isLoading || archiveMutation.isLoading}
              onNavigate={(scenarioId) => navigate({ to: `/devtools/monitors/${scenarioId}` })}
            />
          ))
        )}
      </div>
    </div>
  )
}

// Summary Card Component
function SummaryCard({
  label,
  count,
  color,
  onClick,
  active,
}: {
  label: string
  count: number
  color: "green" | "yellow" | "red"
  onClick: () => void
  active: boolean
}) {
  const colorClasses = {
    green: "border-l-green-500 bg-green-500/10",
    yellow: "border-l-yellow-500 bg-yellow-500/10",
    red: "border-l-red-500 bg-red-500/10",
  }

  const textColors = {
    green: "text-green-500",
    yellow: "text-yellow-500",
    red: "text-red-500",
  }

  return (
    <button
      data-testid={`monitors-summary-${label.toLowerCase()}`}
      onClick={onClick}
      className={`rounded-lg p-4 border-l-4 ${colorClasses[color]} ${
        active ? "ring-2 ring-primary" : ""
      } transition-all hover:opacity-80 text-left`}
    >
      <div className={`text-xs font-medium ${textColors[color]}`}>{label}</div>
      <div className={`text-3xl font-bold mt-1 ${textColors[color]}`}>{count}</div>
    </button>
  )
}

// Filter Dropdown Component
function FilterDropdown({
  label,
  value,
  options,
  onChange,
  icon,
}: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
  icon: React.ReactNode
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none bg-secondary border border-border rounded-md px-3 py-1.5 pr-8 text-sm text-foreground cursor-pointer hover:border-border focus:outline-hidden focus:ring-2 focus:ring-primary"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
    </div>
  )
}

// Environment Badge Component - shows enabled/disabled status
function EnvironmentBadge({
  env,
  config,
}: {
  env: "local" | "dev" | "prod"
  config?: { url: string; enabled: boolean }
}) {
  if (!config) {
    // No config synced - show as unknown
    return <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-500/20 text-gray-400">{env}?</span>
  }

  const isEnabled = config.enabled
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
        isEnabled
          ? env === "prod"
            ? "bg-green-500/20 text-green-400"
            : env === "dev"
              ? "bg-yellow-500/20 text-yellow-400"
              : "bg-blue-500/20 text-blue-400"
          : "bg-red-500/20 text-red-400 line-through"
      }`}
      title={isEnabled ? config.url : `${env} disabled`}
    >
      {env}
    </span>
  )
}

// Group Row Component
function GroupRow({
  group,
  isExpanded,
  onToggle,
  onTriggerRun,
  onArchive,
  onSnooze,
  onUnsnooze,
  isPending,
  onNavigate,
}: {
  group: ScenarioGroup
  isExpanded: boolean
  onToggle: () => void
  onTriggerRun: (id: string) => void
  onArchive: (id: string) => void
  onSnooze: (id: string, duration: "1h" | "24h" | "7d") => void
  onUnsnooze: (id: string) => void
  isPending: boolean
  onNavigate: (id: string) => void
}) {
  const progressPercent = group.totalCount > 0 ? (group.passCount / group.totalCount) * 100 : 0
  const envConfig = group.projectConfig?.environments

  return (
    <div className="border-b border-border">
      {/* Group Header */}
      <div onClick={onToggle} className="flex items-center py-3 cursor-pointer hover:bg-secondary transition-colors">
        <div className="flex items-center flex-1 min-w-0 gap-3">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          )}
          <Folder className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="font-medium text-foreground truncate">{group.name}</span>
          <Badge className="bg-border text-muted-foreground">{group.totalCount} checks</Badge>

          {/* Environment badges with enabled/disabled status */}
          <div className="flex items-center gap-1">
            <EnvironmentBadge env="local" config={envConfig?.local} />
            <EnvironmentBadge env="dev" config={envConfig?.dev} />
            <EnvironmentBadge env="prod" config={envConfig?.prod} />
          </div>

          {group.lastRun && <span className="text-xs text-muted-foreground">at {formatDate(group.lastRun)}</span>}
        </div>
        {/* Progress Bar */}
        <div className="w-64 h-1.5 bg-border rounded-full overflow-hidden mx-4">
          <div
            className={`h-full transition-all ${
              progressPercent === 100 ? "bg-green-500" : progressPercent > 0 ? "bg-yellow-500" : "bg-red-500"
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className="w-10" />
      </div>

      {/* Expanded Scenarios */}
      {isExpanded && (
        <div className="pl-8">
          {group.scenarios.map((scenario) => (
            <ScenarioRow
              key={scenario.id}
              scenario={scenario}
              onTriggerRun={onTriggerRun}
              onArchive={onArchive}
              onSnooze={onSnooze}
              onUnsnooze={onUnsnooze}
              isPending={isPending}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// Scenario Row Component
function ScenarioRow({
  scenario,
  onTriggerRun,
  onArchive,
  onSnooze,
  onUnsnooze,
  isPending,
  onNavigate,
}: {
  scenario: ScenarioItem
  onTriggerRun: (id: string) => void
  onArchive: (id: string) => void
  onSnooze: (id: string, duration: "1h" | "24h" | "7d") => void
  onUnsnooze: (id: string) => void
  isPending: boolean
  onNavigate: (id: string) => void
}) {
  const [menuOpen, setMenuOpen] = useState(false)

  // Environment badge color
  const envBadgeClass =
    scenario.environment === "prod"
      ? "bg-green-500/20 text-green-400"
      : scenario.environment === "dev"
        ? "bg-blue-500/20 text-blue-400"
        : "bg-gray-500/20 text-gray-400"

  return (
    <div className="border-t border-border">
      {/* Main Row */}
      <div
        data-testid="monitors-scenario-row"
        className="flex items-center py-3 hover:bg-secondary transition-colors cursor-pointer"
        onClick={() => onNavigate(scenario.id)}
      >
        <div className="flex items-center flex-1 min-w-0 gap-3">
          <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <StatusIcon status={scenario.status} />
          <div className="min-w-0">
            <div className="font-medium text-foreground truncate flex items-center gap-2">
              {scenario.name}
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${envBadgeClass}`}>
                {scenario.environment}
              </span>
              {scenario.isSnoozed && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-500/20 text-yellow-400">
                  snoozed
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {scenario.lastRun ? (
                <>
                  at {formatDate(new Date(scenario.lastRun.createdAt))}
                  {scenario.status === "failing" && <span className="text-red-400 ml-2">• Last failed run</span>}
                </>
              ) : (
                "No check results yet"
              )}
            </div>
          </div>
        </div>
        <div className="w-20 text-center">
          <Badge className="bg-border text-muted-foreground text-xs">API</Badge>
        </div>
        <div className="w-28 text-center text-xs text-muted-foreground">
          {scenario.status === "unknown" ? "PENDING..." : scenario.status.toUpperCase()}
        </div>
        {/* Run history visualization - last 15 runs */}
        <div className="w-24 flex justify-center">
          <RunHistoryBar runs={scenario.recentRuns} />
        </div>
        <div className="w-16 text-right text-xs text-muted-foreground">
          {scenario.lastRun ? `${scenario.lastRun.duration}ms` : "-"}
        </div>
        <div className="w-16 text-right text-xs text-muted-foreground">{formatSchedule(scenario.schedule)}</div>
        <div className="w-10 relative">
          <button
            onClick={(e) => {
              e.stopPropagation()
              setMenuOpen(!menuOpen)
            }}
            className="p-1 hover:bg-border rounded"
          >
            <MoreVertical className="h-4 w-4 text-muted-foreground" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 bg-secondary border border-border rounded-md shadow-lg z-10 py-1 min-w-[150px]">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onTriggerRun(scenario.id)
                  setMenuOpen(false)
                }}
                disabled={isPending}
                className="w-full px-3 py-1.5 text-left text-sm hover:bg-border flex items-center gap-2 disabled:opacity-50"
              >
                <Play className="h-3 w-3" /> Run Now
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onNavigate(scenario.id)
                  setMenuOpen(false)
                }}
                className="w-full px-3 py-1.5 text-left text-sm hover:bg-border flex items-center gap-2"
              >
                <ExternalLink className="h-3 w-3" /> View Details
              </button>
              <div className="border-t border-border my-1" />
              {scenario.isSnoozed ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onUnsnooze(scenario.id)
                    setMenuOpen(false)
                  }}
                  disabled={isPending}
                  className="w-full px-3 py-1.5 text-left text-sm hover:bg-border flex items-center gap-2 text-yellow-400 disabled:opacity-50"
                >
                  <Bell className="h-3 w-3" /> Unsnooze
                </button>
              ) : (
                <>
                  <div className="px-3 py-1 text-[10px] text-muted-foreground uppercase font-medium">Snooze</div>
                  {(["1h", "24h", "7d"] as const).map((d) => (
                    <button
                      key={d}
                      onClick={(e) => {
                        e.stopPropagation()
                        onSnooze(scenario.id, d)
                        setMenuOpen(false)
                      }}
                      disabled={isPending}
                      className="w-full px-3 py-1.5 text-left text-sm hover:bg-border flex items-center gap-2 disabled:opacity-50"
                    >
                      <Moon className="h-3 w-3" /> {d}
                    </button>
                  ))}
                </>
              )}
              <div className="border-t border-border my-1" />
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onArchive(scenario.id)
                  setMenuOpen(false)
                }}
                disabled={isPending}
                className="w-full px-3 py-1.5 text-left text-sm hover:bg-border flex items-center gap-2 text-red-400 disabled:opacity-50"
              >
                <Archive className="h-3 w-3" /> Archive
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Status Icon Component
function StatusIcon({ status }: { status: ScenarioItem["status"] }) {
  switch (status) {
    case "healthy":
      return <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
    case "failing":
      return <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
    case "degraded":
      return <Clock className="h-5 w-5 text-yellow-500 flex-shrink-0" />
    default:
      return <Clock className="h-5 w-5 text-muted-foreground flex-shrink-0" />
  }
}

// Run History Bar Component - shows last 15 runs as vertical bars
function RunHistoryBar({ runs }: { runs?: { success: boolean; duration: number }[] }) {
  if (!runs || runs.length === 0) {
    // Show placeholder when no runs
    return (
      <div className="flex items-end gap-0.5 h-4" title="No run history">
        {Array.from({ length: 15 }).map((_, i) => (
          <div key={i} className="w-1 bg-border rounded-sm" style={{ height: "4px" }} />
        ))}
      </div>
    )
  }

  // Pad with empty slots if less than 15 runs
  const paddedRuns = [...Array(Math.max(0, 15 - runs.length)).fill(null), ...runs]

  return (
    <div
      className="flex items-end gap-0.5 h-4"
      title={`${runs.filter((r) => r?.success).length}/${runs.length} passed`}
    >
      {paddedRuns.map((run, i) => (
        <div
          key={i}
          className={`w-1 rounded-sm transition-all ${
            run === null ? "bg-border" : run.success ? "bg-green-500" : "bg-red-500"
          }`}
          style={{
            height: run === null ? "4px" : "16px",
          }}
          title={run ? `${run.success ? "Pass" : "Fail"} - ${run.duration}ms` : "No data"}
        />
      ))}
    </div>
  )
}

// Badge Component
function Badge({ children, className }: { children: React.ReactNode; className?: string }) {
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${className}`}>{children}</span>
}

// Helper functions
function formatDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function formatSchedule(schedule: Schedule): string {
  // Handle object format with start time
  if (typeof schedule === "object") {
    const intervalStr = formatInterval(schedule.interval)
    return `${intervalStr} @ ${schedule.startTime}`
  }
  return formatInterval(schedule)
}

function formatInterval(interval: string): string {
  switch (interval) {
    case "1m":
      return "1 min"
    case "5m":
      return "5 min"
    case "15m":
      return "15 min"
    case "1h":
      return "1h"
    case "3h":
      return "3h"
    case "6h":
      return "6h"
    case "24h":
      return "24h"
    default:
      return interval
  }
}

// Project Environment Card Component
function ProjectEnvironmentCard({
  config,
  onToggle,
  isToggling,
}: {
  config: {
    project: string
    displayName: string
    environments?: {
      local?: { url: string; enabled: boolean }
      dev?: { url: string; enabled: boolean }
      prod?: { url: string; enabled: boolean }
    } | null
  }
  onToggle: (env: EnvironmentType, enabled: boolean) => void
  isToggling: boolean
}) {
  const environments = config.environments || {}

  return (
    <div className="bg-secondary rounded-lg p-4 border border-border">
      <div className="flex items-center gap-2 mb-3">
        <Globe className="h-4 w-4 text-muted-foreground" />
        <h3 className="font-medium text-foreground">{config.displayName || config.project}</h3>
      </div>

      <div className="space-y-2">
        {(["prod", "dev", "local"] as const).map((envKey) => {
          const envConfig = environments[envKey]
          if (!envConfig) return null

          return (
            <div key={envKey} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${envConfig.enabled ? "bg-green-500" : "bg-gray-500"}`}
                />
                <span className="font-mono text-xs text-muted-foreground uppercase w-12">{envKey}</span>
                <code className="text-xs bg-background px-1.5 py-0.5 rounded truncate flex-1" title={envConfig.url}>
                  {envConfig.url.replace(/^https?:\/\//, "")}
                </code>
              </div>
              <button
                onClick={() => onToggle(envKey, !envConfig.enabled)}
                disabled={isToggling}
                className={`flex-shrink-0 ml-2 p-1 rounded transition-colors ${
                  envConfig.enabled
                    ? "text-green-500 hover:text-green-400"
                    : "text-muted-foreground hover:text-foreground"
                } ${isToggling ? "opacity-50 cursor-not-allowed" : ""}`}
                title={envConfig.enabled ? "Click to disable" : "Click to enable"}
              >
                {envConfig.enabled ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
              </button>
            </div>
          )
        })}

        {!environments.prod && !environments.dev && !environments.local && (
          <div className="text-xs text-muted-foreground italic">No environments configured</div>
        )}
      </div>
    </div>
  )
}

export default MonitorsScreen
